import React, { useState, useEffect } from "react";
import * as api from "../../api/index";
import { jwtDecode } from "jwt-decode";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";

const Sellerorders = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("current");
  const [ordersData, setOrdersData] = useState({ current: [], completed: [], pending: [] });
  const [pagination, setPagination] = useState({ page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [buttonState, setButtonState] = useState({});

  let id = null;
  const limit = 20;

  try {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = jwtDecode(token);
      id = decoded.id || decoded._id;
    }
  } catch (err) {
    setErrorMsg("Authentication error. Please log in again.");
  }

  const fetchOrders = async (page = 1) => {
    if (!id) return;
    try {
      setLoading(true);
      setErrorMsg("");
      const res = await api.Getsellerorders(id, page, limit);

      setOrdersData({
        current: res.data.current || [],
        completed: res.data.completed || [],
        pending: res.data.pending || [],
      });

      setPagination(res.data.pagination || { page: 1, pages: 1 });

      const states = {};
      res.data.current?.forEach((order) => {
        states[order._id] = {
          ProductPicked: order.tracking?.find((t) => t.stage === "Product Picked")?.done,
          OntheWay: order.tracking?.find((t) => t.stage === "On the Way")?.done,
        };
      });
      setButtonState(states);
    } catch (error) {
      setErrorMsg("Failed to load orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(1);
  }, [id]);

  const normalizeStage = (stage) => {
    if (stage === "Product Picked") return "ProductPicked";
    if (stage === "On the Way") return "OntheWay";
    return stage.replace(/\s+/g, "");
  };

  const updateStage = async (orderId, stage) => {
    const key = normalizeStage(stage);
    setButtonState((prev) => ({ ...prev, [orderId]: { ...prev[orderId], [key]: true } }));

    try {
      await api.updateOrderStage(orderId, stage);
    } catch (err) {
      setErrorMsg("Could not update order stage.");
    }
  };

  const renderOrders = (orders, showButtons) => (
    <div className="space-y-3 mt-4">
      {loading ? (
        <p className="text-gray-500">Loading...</p>
      ) : errorMsg ? (
        <p className="text-red-500">{errorMsg}</p>
      ) : orders.length === 0 ? (
        <p className="text-gray-500">No orders found.</p>
      ) : (
        orders.map((order) => (
          <div
            key={order._id}
            className="p-4 bg-gray-50 shadow-sm rounded-lg border border-gray-200"
          >
            <h3 className="font-semibold text-md">{order.item}</h3>
            <p className="text-sm text-gray-500">{order.status}</p>

            {showButtons && (
              <div className="mt-2 flex gap-2">
                <button
                  className={`px-3 py-1 rounded text-sm ${
                    buttonState[order._id]?.ProductPicked
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-teal-500 text-white hover:bg-teal-600"
                  }`}
                  disabled={buttonState[order._id]?.ProductPicked}
                  onClick={() => updateStage(order._id, "Product Picked")}
                >
                  Picked
                </button>

                <button
                  className={`px-3 py-1 rounded text-sm ${
                    buttonState[order._id]?.OntheWay
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-green-500 text-white hover:bg-green-600"
                  }`}
                  disabled={buttonState[order._id]?.OntheWay}
                  onClick={() => updateStage(order._id, "On the Way")}
                >
                  On the way
                </button>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen bg-gray-100 p-6">
      <div className="w-full mb-4 flex justify-between items-center">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-700 hover:text-gray-900"
        >
          <IoMdArrowBack size={24} />
          <span className="ml-1">Back</span>
        </button>
      </div>

      <div className="max-w-5xl w-full mx-auto bg-white p-6 rounded-xl shadow-md">
        <h1 className="text-xl font-bold mb-4 text-gray-800">My Orders</h1>

        {/* Tabs (Always on one row) */}
        <div className="flex justify-center gap-6 border-b border-gray-200 pb-2">
          {["current", "completed", "pending"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-2 text-sm ${
                activeTab === tab
                  ? "border-b-2 border-teal-500 text-teal-500 font-semibold"
                  : "text-gray-500 hover:text-teal-500"
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Orders */}
        {activeTab === "current" && renderOrders(ordersData.current, true)}
        {activeTab === "completed" && renderOrders(ordersData.completed)}
        {activeTab === "pending" && renderOrders(ordersData.pending)}

        {/* Pagination */}
        <div className="flex justify-center items-center mt-6 space-x-4">
          <button
            disabled={pagination.page <= 1}
            onClick={() => fetchOrders(pagination.page - 1)}
            className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
          >
            Prev
          </button>

          <span className="text-sm">
            Page {pagination.page} / {pagination.pages}
          </span>

          <button
            disabled={pagination.page >= pagination.pages}
            onClick={() => fetchOrders(pagination.page + 1)}
            className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sellerorders;

